import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Calendar,
  MapPin,
  Quote
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../SEO';

// Logos
const logos = {
  womenBusinessSoiree: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/95iizd24_transparent-fortheminds%20%281%29.png',
  dataDreaming: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/d7ltuzdn_datadreaminglogowhite.png',
  // Tools actually used in the workshop
  notion: 'https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png',
  canva: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/15zeamoo_CP7Z_quEhYkDEAE%3D.webp',
  airtable: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/6if1ce6z_09d47a15534609898b3c26b98664751f.jpg',
  milanote: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/6sre8ahc_PGX9pn9g_400x400.jpg',
  runway: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/wkx9prw2_eca69951-0148-4dd5-ae85-5a1de75251c6_Runway%2Bsquare.avif',
  // Host logos (from public assets folder)
  oceanAi: '/assets/oceanailogowhite.png',
  wine1010: '/assets/1010wines.png',
  oldvineFloral: '/assets/oldvine.png',
};

// Event photos - main image first, then 3 below
const mainPhoto = 'https://customer-assets.emergentagent.com/job_phygital-portal/artifacts/gpxjniud_unnamed%20%282%29.png';
const eventPhotos = [
  'https://customer-assets.emergentagent.com/job_phygital-portal/artifacts/tcg1tn9e_unnamed%20%283%29.png',
  'https://customer-assets.emergentagent.com/job_dream-cloud-editor/artifacts/68562min_womenssoiree.png',
  'https://customer-assets.emergentagent.com/job_phygital-portal/artifacts/6jm6xa89_unnamed%20%285%29.png',
];

// Color palette
const colors = {
  primary: '#E86A4A',
  secondary: '#FDC4E0',
  background: '#221e1e',
  pink: '#FDC4E0',
  coral: '#E86A4A',
};

// Tools used in workshop
const toolsUsed = [
  { name: 'Notion', logo: logos.notion },
  { name: 'Canva', logo: logos.canva },
  { name: 'Airtable', logo: logos.airtable },
  { name: 'Milanote', logo: logos.milanote },
  { name: 'Runway', logo: logos.runway },
];

// Topics covered
const topics = [
  'Operations',
  'Marketing',
  'Customer Service',
];

const WomensSoireeRecap = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: colors.background }}>
      <SEO 
        title="Women Business Soirée Recap | AI for Business Growth"
        description="Recap from our Women Business Soirée event featuring AI workshops, networking, and business growth strategies."
        image={logos.womenBusinessSoiree}
        url="/recap/womens-soiree"
      />
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </Link>
          <img src={logos.dataDreaming} alt="Data Dreaming" className="h-6 object-contain" />
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 md:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.img 
            src={logos.womenBusinessSoiree}
            alt="Women Business Soiree"
            className="h-16 md:h-20 object-contain mx-auto mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <span 
              className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4"
              style={{ background: `${colors.pink}20`, color: colors.pink }}
            >
              Event Recap
            </span>
            
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              AI for Business Growth
            </h1>
            
            <p className="text-lg text-white/70 mb-6 max-w-xl mx-auto">
              An intimate workshop for women business owners exploring practical AI applications.
            </p>

            <div className="flex items-center justify-center gap-4 md:gap-6 text-white/60 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                January 14, 2026
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Los Angeles
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photo Gallery - Main image + 3 below */}
      <section className="px-4 pb-12">
        <div className="max-w-4xl mx-auto">
          {/* Main large photo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-4"
          >
            <div className="aspect-[16/9] rounded-xl md:rounded-2xl overflow-hidden">
              <img 
                src={mainPhoto} 
                alt="Women Business Soiree event"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
          
          {/* 3 smaller photos in a row */}
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            {eventPhotos.map((photo, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="aspect-square rounded-xl md:rounded-2xl overflow-hidden"
              >
                <img 
                  src={photo} 
                  alt={`Event moment ${i + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center p-8 md:p-12 rounded-2xl md:rounded-3xl relative"
            style={{ background: `${colors.coral}15` }}
          >
            <Quote className="w-10 h-10 mx-auto mb-4" style={{ color: colors.coral }} />
            <p className="text-xl md:text-2xl text-white font-light italic mb-6 leading-relaxed">
              "Women Business Soiree was such a safe space to connect and learn with women founders."
            </p>
            <p className="text-white/70">— Rose</p>
          </motion.div>
        </div>
      </section>

      {/* Topics & Tools */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Topics Covered */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 md:p-8 rounded-2xl bg-white/5"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Topics Covered</h3>
              <div className="flex flex-wrap gap-2">
                {topics.map((topic, i) => (
                  <span 
                    key={i}
                    className="px-4 py-2 rounded-full text-sm font-medium"
                    style={{ background: `${colors.pink}20`, color: colors.pink }}
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Tools Demonstrated */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-6 md:p-8 rounded-2xl bg-white/5"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Tools Demonstrated</h3>
              <div className="flex flex-wrap gap-3">
                {toolsUsed.map((tool, i) => (
                  <div 
                    key={i}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10"
                  >
                    <img src={tool.logo} alt={tool.name} className="w-6 h-6 rounded object-contain" />
                    <span className="text-sm text-white/80">{tool.name}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hosts - Logos Only */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-white/60 text-sm uppercase tracking-wider mb-8">Hosted By</p>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              <img src={logos.wine1010} alt="1010 Wine" className="h-12 md:h-16 object-contain opacity-80 hover:opacity-100 transition-opacity" />
              <img src={logos.oceanAi} alt="Ocean AI" className="h-10 md:h-14 object-contain opacity-80 hover:opacity-100 transition-opacity" />
              <img src={logos.oldvineFloral} alt="The Oldvine Floral" className="h-12 md:h-16 object-contain opacity-80 hover:opacity-100 transition-opacity" />
              <img src={logos.dataDreaming} alt="Data Dreaming" className="h-8 md:h-10 object-contain opacity-80 hover:opacity-100 transition-opacity" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Workshop Access CTA */}
      <section className="py-12 px-4 pb-20">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-3xl text-center"
            style={{ 
              background: 'linear-gradient(135deg, rgba(232, 106, 74, 0.2) 0%, rgba(253, 196, 224, 0.2) 100%)',
              border: '1px solid rgba(232, 106, 74, 0.3)'
            }}
          >
            <h3 className="text-xl font-semibold text-white mb-2">Attended the event?</h3>
            <p className="text-white/60 mb-6">
              You have exclusive access to the full AI for Business Growth workshop materials.
            </p>
            <Link
              to="/womenbusinesssoiree"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium text-white transition-all hover:scale-105"
              style={{ background: colors.coral }}
            >
              Access Workshop Materials
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WomensSoireeRecap;
